import { Identify } from '@amplitude/analytics-browser';

import type { AmplitudeEnrichmentProperties, AmplitudeEvent } from './taxonomy';
import { EVENTS, PROPERTIES } from './taxonomy';
import type { AnalyticsConfig, AnalyticsProvider, EventProperties, UserProperties } from './types';

// Type import for the lazily-loaded SDK
type AmplitudeUnified = typeof import('@amplitude/unified');

/**
 * Enrichment plugin callback. Merges org and agent properties into all events.
 *
 * Only enriches when `Organization` is present in the context.
 * Context properties are spread first so caller-supplied event properties
 * take precedence over the enrichment defaults.
 */
export function enrichEventWithOrgAgent(
  event: AmplitudeEvent,
  context: AmplitudeEnrichmentProperties
): AmplitudeEvent {
  if (context.Organization) {
    event.event_properties = {
      ...context,
      ...event.event_properties,
    };
  }
  return event;
}

/**
 * Enrichment plugin callback. Events in our taxonomy get property-validated
 * (unknown keys are stripped with a warning). Everything else (autocaptured,
 * $identify, etc.) passes through untouched.
 */
export function filterEvent(event: AmplitudeEvent): AmplitudeEvent | null {
  const { event_type, event_properties } = event;

  if (!EVENTS.has(event_type)) return event;

  if (!event_properties) return event;

  const allowed = PROPERTIES[event_type];
  if (!allowed) return event;

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event_properties)) {
    if (allowed.has(key)) {
      filtered[key] = value;
    } else {
      console.warn(`Dropping unknown property "${key}" from Amplitude event "${event_type}"`);
    }
  }
  return { ...event, event_properties: filtered };
}

/**
 * Amplitude analytics provider implementation with lazy-loaded SDK
 *
 * Uses the @amplitude/unified package with initAll() to initialize
 * analytics and session replay together.
 */
export class AmplitudeProvider implements AnalyticsProvider {
  readonly name = 'Amplitude';
  private active = false;
  private initialized = false;
  private amplitude: AmplitudeUnified | null = null;

  /** Context derived from identify() calls, merged into every event by the enrichment plugin. */
  private enrichmentContext: AmplitudeEnrichmentProperties = {};

  async init(config: AnalyticsConfig): Promise<void> {
    const { isActive, apiKey } = config;

    if (!isActive) {
      return;
    }

    if (this.initialized) {
      console.warn('Amplitude already initialized');
      return;
    }

    // Lazy load the Amplitude unified SDK only when init() is called
    this.amplitude = await import('@amplitude/unified');

    // Taxonomy gate: validate properties on known events, pass through autocaptured.
    // Registered before enrichment so it only sees caller-supplied properties.
    this.amplitude.add({
      name: 'taxonomy-gate',
      type: 'enrichment' as const,
      execute: async (event) => filterEvent(event as AmplitudeEvent) as any,
    });

    // Enrichment plugin that tags every event with org/agent from the enrichmentContext,
    // which is populated by identify() calls driven by router state changes.
    this.amplitude.add({
      name: 'org-agent-enrichment',
      type: 'enrichment' as const,
      execute: async (event) =>
        enrichEventWithOrgAgent(event as AmplitudeEvent, this.enrichmentContext),
    });

    const relay = '/bff/relay/amplitude';

    this.amplitude.initAll(apiKey, {
      analytics: {
        serverUrl: `${relay}/a/2/httpapi`,
        remoteConfig: {
          serverUrl: `${relay}/rc/config`,
        },
        autocapture: {
          attribution: true,
          fileDownloads: true,
          formInteractions: true,
          pageViews: true,
          sessions: true,
          elementInteractions: true,
          networkTracking: true,
          webVitals: true,
          frustrationInteractions: true,
        },
      },
      sessionReplay: {
        sampleRate: 1,
        trackServerUrl: `${relay}/sr/sessions/v2/track`,
        configServerUrl: `${relay}/src/config`,
      },
      engagement: {
        serverUrl: `${relay}/gs`,
        cdnUrl: `${relay}/cdn`,
        mediaUrl: `${relay}/cdn`,
      },
    });

    this.active = isActive;
    this.initialized = true;
  }

  identify(userId: string, properties?: UserProperties): void {
    if (!this.active || !this.initialized || !this.amplitude) return;

    const { displayName, email, uuid, orgShortName, agentShortName } = properties ?? {};

    // Update enrichment context so the plugin tags subsequent events
    if (orgShortName) {
      this.enrichmentContext.Organization = orgShortName;
      this.enrichmentContext.Agent = agentShortName
        ? `${orgShortName}-${agentShortName}`
        : undefined;
    }

    // Set Amplitude user identity and properties (skip if this is a context-only update)
    if (userId) {
      const identifyEvent = new Identify();
      if (displayName) identifyEvent.set('Display Name', displayName);
      if (email) identifyEvent.set('Email', email);
      if (uuid) identifyEvent.set('UUID', uuid);

      this.amplitude.setUserId(userId);
      this.amplitude.identify(identifyEvent);
    }
  }

  track(eventName: string, properties?: EventProperties): void {
    if (!this.active || !this.initialized || !this.amplitude) return;
    if (!EVENTS.has(eventName)) {
      console.warn(`Unknown Amplitude event: "${eventName}"`);
      return;
    }
    this.amplitude.track(eventName, properties);
  }

  deidentify(): void {
    if (!this.active || !this.initialized || !this.amplitude) return;
    this.amplitude.setUserId(undefined);
  }

  isActive(): boolean {
    return this.active && this.initialized;
  }
}
